import React, { useState, useEffect, useRef } from 'react';
import styles from './SupportChat.module.scss';
import { siteStructure } from 'constants/siteStructure';
import { useNavigate } from 'react-router-dom';

const SupportChat = ({ isOpen, onClose }) => {
    const [messages, setMessages] = useState([
        { id: 1, text: 'Привет! Я ваш виртуальный помощник. Что вы хотите исследовать или найти на платформе?', sender: 'bot' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (chatContainerRef.current && !chatContainerRef.current.contains(event.target)) {
                // Check if the click was on the toggle button (which we can't easily ref here without context)
                // specific hack: check if the target is NOT the toggle button in App.js
                // The toggle button usually has a specific SVG or class. 
                // However, since App.js handles the toggle, if we call onClose here, App.js will setOpen(false).
                // If the user clicked the toggle button, App.js might toggle it back to true?
                // Let's rely on the fact that if this is open, a click outside closes it. 
                // To avoid conflict with the toggle button, we can check if the click target is the toggle button.
                // Or better, assume standard "click outside" behavior.
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);



    // Basic Chit-Chat patterns
    const chitChatPatterns = [
        { triggers: ['привет', 'здравствуй', 'hello', 'hi', 'салам', 'ку', 'эу'], response: 'Привет! Чем я могу помочь вам сегодня?' },
        { triggers: ['как дела', 'как ты', 'how are you', 'how r u', 'кандай'], response: 'У меня всё отлично, ведь я часть NCORE! А у вас как?' },
        { triggers: ['кто ты', 'ты кто', 'who are you', 'what are you'], response: 'Я виртуальный помощник NCORE. Я здесь, чтобы помочь вам найти нужные сервисы и ответить на вопросы.' },
        { triggers: ['спасибо', 'благодарю', 'thanks', 'thx', 'рахмат', 'спс'], response: 'Всегда пожалуйста! Обращайтесь, если будет нужно что-то еще.' },
        { triggers: ['пока', 'до свидания', 'bye', 'goodbye', 'корушкончо'], response: 'До свидания! Возвращайтесь скорее!' },
        { triggers: ['круто', 'классно', 'супер', 'cool', 'awesome', 'зынк'], response: 'Рад, что вам нравится! Мы стараемся для вас.' },
        { triggers: ['помоги', 'help', 'помощь', 'жардам'], response: 'Конечно! Просто напишите, что вы ищете или какой у вас вопрос.' },
        { triggers: ['имя', 'зовут', 'name'], response: 'Меня зовут NCORE Bot v1.0, но для друзей просто Бот.' }
    ];

    const getChitChatResponse = (input) => {
        const lowerInput = input.toLowerCase();
        for (const pattern of chitChatPatterns) {
            if (pattern.triggers.some(trigger => lowerInput.includes(trigger))) {
                return pattern.response;
            }
        }
        return null;
    };

    const handleSend = () => {
        if (!inputValue.trim()) return;

        const userMsg = { id: Date.now(), text: inputValue, sender: 'user' };
        setMessages(prev => [...prev, userMsg]);
        setInputValue('');
        setIsTyping(true);

        // Analyze logic
        setTimeout(() => {
            const query = userMsg.text.toLowerCase();
            
            // 1. Try Site Structure Search
            const searchResult = searchStructure(query);
            
            let botResponse;
            
            if (searchResult) {
                botResponse = {
                    id: Date.now() + 1,
                    sender: 'bot',
                    text: `Я нашел то, что вы искали! Это раздел "${searchResult.title}".`,
                    link: searchResult.path,
                    linkText: 'Перейти'
                };
            } else {
                // 2. Try Chit-Chat
                const chatResponse = getChitChatResponse(query);
                
                if (chatResponse) {
                    botResponse = {
                        id: Date.now() + 1,
                        sender: 'bot',
                        text: chatResponse
                    };
                } else {
                    // 3. Fallback
                    botResponse = {
                        id: Date.now() + 1,
                        sender: 'bot',
                        text: 'К сожалению, я не нашел то, что вы просили. Я пока плохо работаю, но обещаю скоро во всём разобраться и стать наилучшей версией себя!'
                    };
                }
            }

            setMessages(prev => [...prev, botResponse]);
            setIsTyping(false);
        }, 1500); // Fake delay for realism
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSend();
    };

    // Flatten structure for search
    const getAllNodes = (nodes) => {
        let all = [];
        nodes.forEach(node => {
            all.push(node);
            if (node.children) {
                all = [...all, ...getAllNodes(node.children)];
            }
        });
        return all;
    };

    const searchStructure = (query) => {
        const normalizedQuery = query.toLowerCase().trim();
        const allNodes = getAllNodes(siteStructure);
        
        // Helper specifically for partial matching
        // Ensures short inputs like "ser" match "server"
        // But also ensures we don't match everything if query is 1 char
        const isMatch = (keyword, input) => {
            if (input.length < 2) return keyword === input; // Too short, exact match only
            return keyword.includes(input) || input.includes(keyword); 
        };

        // 1. Keyword Fuzzy Match
        const keywordMatch = allNodes.find(node => 
            node.keywords && node.keywords.some(k => isMatch(k.toLowerCase(), normalizedQuery))
        );
        if (keywordMatch) return keywordMatch;

        // 2. Title Fuzzy Match
        const titleMatch = allNodes.find(node => 
            isMatch(node.title.toLowerCase(), normalizedQuery)
        );
        if (titleMatch) return titleMatch;

        // 3. Description (checking if query is inside description)
        const descMatch = allNodes.find(node => 
            node.description && node.description.toLowerCase().includes(normalizedQuery)
        );
        if (descMatch) return descMatch;

        return null;
    };

    if (!isOpen) return null;

    return (
        <div className={styles.chatContainer} ref={chatContainerRef}>
            <div className={styles.header}>
                <div className={styles.title}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C13.66 6 15 7.34 15 9C15 10.66 13.66 12 12 12C10.34 12 9 10.66 9 9C9 7.34 10.34 6 12 6ZM12 20C9.33 20 7 18.67 7 16.5C7 14.83 9.33 13.5 12 13.5C14.67 13.5 17 14.83 17 16.5C17 18.67 14.67 20 12 20Z" fill="#667eea"/>
                    </svg>
                    NCORE Support
                </div>
                <button className={styles.closeButton} onClick={onClose}>×</button>
            </div>

            <div className={styles.messages}>
                {messages.map(msg => (
                    <div key={msg.id} className={`${styles.message} ${msg.sender === 'user' ? styles.userMessage : styles.botMessage}`}>
                        {msg.text}
                        {msg.link && (
                            <div style={{ marginTop: '8px' }}>
                                <a 
                                    className={styles.linkButton}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate(msg.link);
                                        // Optional: Close chat on navigation if needed
                                        // onClose(); 
                                    }}
                                    href={msg.link}
                                >
                                    {msg.linkText}
                                </a>
                            </div>
                        )}
                    </div>
                ))}
                {isTyping && (
                    <div className={`${styles.message} ${styles.botMessage}`}>
                        <span style={{ fontSize: '20px', lineHeight: '10px' }}>...</span>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className={styles.inputArea}>
                <input 
                    type="text" 
                    className={styles.input} 
                    placeholder="Напишите запрос..." 
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                <button className={styles.sendButton} onClick={handleSend} disabled={!inputValue.trim()}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default SupportChat;
